import { useQuery, useQueryClient } from '@tanstack/react-query';
import isEqual from 'react-fast-compare';
import { Address } from 'viem';
import { DEFAULT_SUBACCOUNT } from '../constants';
import { SchemaSubaccountDto } from '../generated/apiTypes';
import { apiClient } from '../lib/apiClient';
import { hasRegisteredSubaccount } from '../lib/subaccountHelpers';
import { activeSignerQueryParams } from './useActiveSigner';
import {
  useActiveSelectedSubaccount,
  usePendingSubaccount,
  useSelectedSubaccount,
} from './useSelectedSubaccount';
import { SubaccountData, useWalletStorage } from './useWalletStorage';

const SUBACCOUNT_POLL_INTERVAL = 3000;

function shouldPoll(
  selectedSubaccount: SchemaSubaccountDto | null,
  subaccounts: SchemaSubaccountDto[] | undefined
) {
  return !selectedSubaccount || !hasRegisteredSubaccount(subaccounts)
    ? SUBACCOUNT_POLL_INTERVAL
    : false;
}

async function fetchActiveSigners(
  queryClient: ReturnType<typeof useQueryClient>,
  subaccountId: string
) {
  const { data: activeSigners } = await queryClient.fetchQuery(
    activeSignerQueryParams(subaccountId)
  );
  return activeSigners;
}

export function subaccountQueryOptions(address: Address | undefined) {
  return apiClient.queryOptions(
    'get',
    '/v1/subaccount',
    { params: { query: { sender: address || '0x0' } } },

    {
      enabled: Boolean(address),
      /* Store in cache for 3 seconds, this helps with the onboard logic, making sure that is you swtich from wallet 1 to wallet 2 and back to wallet 1 we don't get a Get Started flash*/
      gcTime: 3000,
    }
  );
}

async function ensureSubaccountData(
  queryClient: ReturnType<typeof useQueryClient>,
  address: Address,
  subaccountId: string,
  getSubaccountData: (addr: string, id: string) => unknown,
  updateSubaccountData: (addr: string, id: string, data: SubaccountData) => void
) {
  const walletData = getSubaccountData(address, subaccountId);
  if (walletData) return;

  const activeSigners = await fetchActiveSigners(queryClient, subaccountId);
  if (activeSigners.length > 0) {
    // We have a signer but no wallet data => assume manual mode
    updateSubaccountData(address, subaccountId, { signingMethod: 'manual' });
  }
}

/**
 *
 * This hook fetches and maintains the list of subaccounts associated with a given address.
 * It ensures that the selected subaccount is always valid and up-to-date, creating a new
 * subaccount selection if none is currently selected. Additionally, it automatically updates
 * subaccount data based on active signer information, assuming 'manual' signing if active signers
 * exist but no data has been stored. The query key includes the address, ensuring that the state
 * updates correctly when the user switches wallets.
 */
export function useSubaccounts(address: Address | undefined) {
  const { setValue: setSelectedSubaccount, removeValue: removeSelectedSubaccount } =
    useSelectedSubaccount();
  const { setValue: setPendingSubaccount, value: pendingSubaccount } = usePendingSubaccount();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { updateSubaccountData, getSubaccountData } = useWalletStorage();
  const queryClient = useQueryClient();

  const queryOptions = subaccountQueryOptions(address);

  return useQuery({
    ...queryOptions,
    queryFn: async (args) => {
      if (!address) throw new Error('Expected address to be defined');

      const queryResult = await queryOptions.queryFn(args);
      const subaccounts = queryResult.data || [];
      // Default to 'primary' subaccount, falling back to first subaccount.
      const getDefaultSubaccount = () => {
        const s =
          subaccounts.find((subaccount) => subaccount.name === DEFAULT_SUBACCOUNT) ??
          subaccounts[0];
        return s?.registeredBlockNumber ? s : undefined;
      };
      const defaultSubaccount = getDefaultSubaccount();

      // Case 1: Have pending subaccount
      if (!selectedSubaccount && pendingSubaccount) {
        const matchingPendingSubaccount = subaccounts.find(
          ({ name }) => name === pendingSubaccount
        );
        if (matchingPendingSubaccount) {
          setSelectedSubaccount(matchingPendingSubaccount);
          setPendingSubaccount(null);
          await ensureSubaccountData(
            queryClient,
            address,
            matchingPendingSubaccount.id,
            getSubaccountData,
            updateSubaccountData
          );
        }
        return queryResult;
      }

      // Case 2: No subaccounts available
      if (!defaultSubaccount) {
        if (selectedSubaccount) {
          removeSelectedSubaccount();
        }
        return queryResult;
      }

      // Case 3: No currently selected subaccount
      if (!selectedSubaccount) {
        setSelectedSubaccount(defaultSubaccount);
        await ensureSubaccountData(
          queryClient,
          address,
          defaultSubaccount.id,
          getSubaccountData,
          updateSubaccountData
        );
        return queryResult;
      }

      // Case 4: Currently selected subaccount no longer valid
      const matchingSubaccount =
        selectedSubaccount && subaccounts.find(({ id }) => id === selectedSubaccount.id);
      if (!matchingSubaccount) {
        setSelectedSubaccount(defaultSubaccount);
        await ensureSubaccountData(
          queryClient,
          address,
          defaultSubaccount.id,
          getSubaccountData,
          updateSubaccountData
        );
        return queryResult;
      }

      // Case 5: Have matchingSubaccount but it's not equal to the selected one
      if (!isEqual(matchingSubaccount, selectedSubaccount)) {
        setSelectedSubaccount(matchingSubaccount);
      }

      // At this point selectedSubaccount should be correct, now ensure we have subaccount data too
      await ensureSubaccountData(
        queryClient,
        address,
        matchingSubaccount.id,
        getSubaccountData,
        updateSubaccountData
      );

      return queryResult;
    },
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      return shouldPoll(selectedSubaccount, data);
    },
  });
}
